import {
  EastOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  KeyboardDoubleArrowDownOutlined,
  KeyboardDoubleArrowUpOutlined,
} from "@mui/icons-material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { blue, blueGrey, grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NumberInput from "../../../_src_shared/components/inputs/NumberInput";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import DateInput from "../../../components/inputs/DateInput";
import FileUploadField from "../../../components/inputs/FileUploadField";
import SearchInput from "../../../components/inputs/SearchInput";
import SelectFileAssignmentInput from "../../../components/inputs/SelectFileAssignmentInput";
import InputWrapper from "../../../components/inputs/shared/InputWrapper";
import Flx from "../../../components/layout/Flx";
import BasicModal from "../../../components/modals/BasicModal";
import ToggleTabSwitcher from "../../../components/navigation/ToggleTabSwitcher";
import Txt from "../../../components/typography/Txt";

import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import LoanDocumentGroupIcon from "./LoanDocumentGroupIcon";
import { useFileDocGroupAndTypesHook } from "../../../hooks/useFileDocGroupAndTypesHook";
import { Divider } from "@mui/material";
import ChipItem from "../../../components/ui/ChipItem";

const UPLOAD_BORDER_SIZE = 1;

const LoanBulkFileUploader = ({
  show,
  onClose,
  docGroup = "All Categories",
}) => {
  const [filesList, setFilesList] = useState(null);
  const [docGroupFilter, setDocGroupFilter] = useState(docGroup);
  const [quickFilter, setQuickFilter] = useState(null);

  // Replace expandType with a command that changes every click
  const [expandCmd, setExpandCmd] = useState({ type: null, nonce: 0 }); // type: 'open' | 'closed' | null

  const onClearFiles = () => {
    setFilesList(null);
    setDocGroupFilter("All Categories");
  };

  const onModalClose = useCallback(() => {
    setFilesList(null);
    setDocGroupFilter("All Categories");
    onClose(false);
  }, [onClose]);

  const onFilesAdded = useCallback(
    (newFiles) => {
      if (isEmpty(filesList)) {
        setFilesList(newFiles);
        return;
      }
      setFilesList([...filesList, ...newFiles]);
    },
    [filesList],
  );

  const updateFilesList = useCallback((updater) => {
    setFilesList((prevFiles) => {
      if (typeof updater === "function") return updater(prevFiles);
      return updater;
    });
  }, []);

  return (
    <ModalRoot show={show} onClose={onModalClose}>
      <SectionFileUpload
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        onFilesAdded={onFilesAdded}
        expandCmd={expandCmd}
        setExpandCmd={setExpandCmd}
        docGroupFilter={docGroupFilter}
      />

      <DocGroupVerticalNav
        docGroupFilter={docGroupFilter}
        setDocGroupFilter={setDocGroupFilter}
      />

      <AddedFilesDrawer
        onClearFiles={onClearFiles}
        files={filesList}
        updateFiles={updateFilesList}
        onModalClose={onModalClose}
      />
    </ModalRoot>
  );
};

const SectionFileUpload = ({
  quickFilter,
  setQuickFilter,
  onFilesAdded,
  docGroupFilter,
  expandCmd,
  setExpandCmd,
}) => {
  const { getDocTypesByGroup, searchDocumentsAdvanced, fileDocGroupAndTypes } =
    useFileDocGroupAndTypesHook();
  const filteredGroupsAndTypes = useMemo(() => {
    if (docGroupFilter && docGroupFilter !== "All Categories") {
      if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
        return [docGroupFilter];
      }
      const searchResults = searchDocumentsAdvanced(quickFilter);
      if (searchResults[docGroupFilter]) return [docGroupFilter];
      return [];
    }

    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
      return Object.keys(fileDocGroupAndTypes);
    }

    return Object.keys(searchDocumentsAdvanced(quickFilter));
  }, [quickFilter, docGroupFilter]);

  const triggerExpand = (type) => {
    setExpandCmd((prev) => ({ type, nonce: prev.nonce + 1 }));
  };

  return (
    <Flx
      column
      g={1}
      sx={{
        flexGrow: 1,
        background: "#f8fafc",
        overflow: "hidden",
        overflowY: "auto",
        height: "100%",
        flexBasis: "680px",
        borderRadius: 1,
      }}
    >
      <Flx column fw gap={2} sx={{ overflowY: "auto", flexGrow: 1 }}>
        <Flx
          fw
          jb
          ac
          gap={6}
          sx={{
            px: 3,
            py: 2,
            position: "sticky",
            top: 0,
            left: 0,
            background: "#f8fafc",
            zIndex: 11,
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder={
              "Search by document type (e.g., Appraisal, Credit, Photo Identification)"
            }
            sx={{
              flexGrow: 1,
              ".MuiInputBase-root": {
                background: "#ffffff",
                borderRadius: "50px !important",
              },
              input: { borderRadius: "50px", fontSize: "11px" },
            }}
          />

          <Flx gap={1} ac>
            <Button
              variant="outlined"
              onClick={() => triggerExpand("closed")}
              startIcon={<KeyboardArrowDownOutlined />}
            >
              Collapse All
            </Button>

            <Button
              variant="outlined"
              onClick={() => triggerExpand("open")}
              startIcon={<KeyboardArrowUpOutlined />}
            >
              Expand All
            </Button>
          </Flx>
        </Flx>

        <Flx column g={2} wrap sx={{ p: 3, pt: 0 }}>
          <Txt secondary>
            Drag and dropping your loan documents into the appropriate
            categories.
          </Txt>

          {isEmpty(filteredGroupsAndTypes) && (
            <Txt secondary sx={{ fontStyle: "italic" }}>
              No results found for the term "{quickFilter}".
            </Txt>
          )}

          <Flx gap={2} wrap>
            {filteredGroupsAndTypes.map((group) => (
              <DocGroupUploadCard
                key={group} // IMPORTANT
                docGroup={group}
                docType={null}
                docGroupFilter={docGroupFilter}
                quickFilter={quickFilter}
                onFilesAdded={onFilesAdded}
                expandCmd={expandCmd} // changed prop
              />
            ))}
          </Flx>
        </Flx>
      </Flx>
    </Flx>
  );
};

const DocGroupUploadCard = ({
  onFilesAdded,
  docGroup,
  docGroupFilter,
  quickFilter,
  expandCmd, // { type, nonce }
}) => {
  const [show, setShow] = useState(false);
  const fileUploadWidth = "200px";
  const { getDocTypesByGroup, searchDocumentsAdvanced, fileDocGroupAndTypes } =
    useFileDocGroupAndTypesHook();
  const docGroupTypes = useMemo(() => {
    const allTypes = getDocTypesByGroup(docGroup);

    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
      return allTypes;
    }

    const normalizedFilter = quickFilter.toLowerCase().trim();
    const filterWords = normalizedFilter.split(/\s+/);

    return allTypes.filter((type) => {
      const normalizedType = type.toLowerCase();
      return filterWords.some((word) => normalizedType.includes(word));
    });
  }, [quickFilter, docGroup]);

  const shouldShowUnassigned = useMemo(() => {
    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ")
      return true;

    const normalizedFilter = quickFilter.toLowerCase().trim();
    const filterWords = normalizedFilter.split(/\s+/);

    return filterWords.some((word) => "unassigned".includes(word));
  }, [quickFilter]);

  useEffect(() => {
    // Forces open when searching or filtered to a specific group
    const hasQuickFilter = !isNil(quickFilter) && quickFilter.trim() !== "";
    if (hasQuickFilter) {
      setShow(true);
      return;
    }

    if (docGroupFilter !== "All Categories") {
      setShow(true);
      return;
    }

    // Apply expand/collapse command; nonce makes it re-run every click
    if (expandCmd?.type === "open") setShow(true);
    if (expandCmd?.type === "closed") setShow(false);
  }, [expandCmd?.nonce, expandCmd?.type, quickFilter, docGroupFilter]);

  if (!shouldShowUnassigned && docGroupTypes.length === 0) return null;

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 1,
        height: "max-content",
        background: "#ffffff",
        p: 1,
        gap: 0.5,
        flexBasis: show ? "100%" : "300px",
        // flexBasis: show && size(docGroupTypes) > 8 ? "100%" : "300px",
        flexGrow: 1,
        ".file-upload-field": {
          flexGrow: 1,
          flexBasis: fileUploadWidth,
          ".upload-text-label": { color: grey[900], fontSize: "11px", p: 1 },
        },
      }}
    >
      <Flx column gap={0.5}>
        <Box
          onClick={() => setShow((s) => !s)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            justifyContent: "space-between",
            transition: "background-color 0.2s ease-in-out",
            p: 0.5,
            borderRadius: 1,
            "&:hover": { background: blueGrey[50] },
          }}
        >
          <Flx ac gap={0.5} sx={{ svg: { fontSize: "16px" } }}>
            <LoanDocumentGroupIcon styled docGroup={docGroup} size={20} />
            <Txt color={grey[600]} fontSize="12px" sx={{ fontWeight: 600 }}>
              {docGroup}
            </Txt>
          </Flx>
          {show ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </Box>
      </Flx>

      {show ? (
        <FlxGrid width="210px" wrap g={0.5}>
          {docGroupTypes.map((type) => (
            <DocTypeFileUploadBox
              key={type}
              docGroup={docGroup}
              docType={type}
              onFilesAdded={onFilesAdded}
            />
          ))}

          {shouldShowUnassigned && (
            <FileUploadField
              onFilesAdded={(f) => onFilesAdded(f, docGroup, null)}
              suppressGrid
              hideUploadButton
              docGroup={docGroup}
              borderSize={UPLOAD_BORDER_SIZE}
              background={"#ffffff"}
              suppressFileList
              minHeight={"72px"}
              uploadText={"Unassigned"}
              suppressUploadButton
            />
          )}
        </FlxGrid>
      ) : null}
    </Card>
  );
};

const DocTypeFileUploadBox = ({ onFilesAdded, docGroup, docType }) => {
  return (
    <FileUploadField
      onFilesAdded={(f) => onFilesAdded(f, docGroup, docType)}
      suppressGrid
      hideUploadButton
      docType={docType}
      borderSize={UPLOAD_BORDER_SIZE}
      borderColor={grey[600]}
      docGroup={docGroup}
      background={"#ffffff"}
      suppressFileList
      minHeight={"72px"}
      uploadText={docType}
      suppressUploadButton
    />
  );
};

const AddedFilesDrawer = ({
  files,
  onClearFiles,
  onModalClose,
  updateFiles,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const ref = useRef();
  const { getDocTypesByGroup, searchDocumentsAdvanced, fileDocGroups } =
    useFileDocGroupAndTypesHook();
  const [selectedRows, setSelectedRows] = useState(null);
  const [minimized, setMinimized] = useState(true);
  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const dispatch = useDispatch();

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 250,
        cellEditor: "agTextCellEditor",
        editable: true,
        checkbox: true,
        // checkbox: minimized ? false : true,
      },
      {
        headerName: "Document Group",
        // hide: minimized,
        field: "docGroup",
        editable: true,
        width: 160,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          values: fileDocGroups || [],
        },
      },
      {
        headerName: "Document Type",
        // hide: minimized,
        field: "docType",
        editable: true,
        width: 160,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(params.data.docGroup || "Appraisal"),
          };
        },
      },
      {
        headerName: "Report Effective Date",
        // hide: minimized,
        field: "reportEffectiveDate",
        editable: true,
        type: "date",
        width: 120,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Report Guideline Expiration Days",
        // hide: minimized,
        field: "reportGuidelineExpirationDays",
        editable: true,
        type: "number",
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
      },
    ],
    [fileDocGroups, getDocTypesByGroup],
  );

  const onClearFilesClick = useCallback(
    (props) => {
      setSelectedRows(null);
      setMinimized(false);
      onClearFiles();
    },
    [onClearFiles],
  );

  const handleUploadFiles = () => {
    uploadLoanDocs({
      loanId: loanDrilldown?._id,
      files: files,
      onSuccessMsg:
        size(files) > 1
          ? `${size(files)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onClearFilesClick();
        onModalClose();
      },
    });
  };

  const handleRowSelectionChange = useCallback((params) => {
    const selected = params.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  const handleDeleteSelectedRows = useCallback(() => {
    if (!selectedRows || selectedRows.length === 0) return;

    const selectedIds = selectedRows.map((r) => r.id);

    updateFiles((prevFiles) =>
      prevFiles.filter((row) => !selectedIds.includes(row.id)),
    );

    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }

    setSelectedRows([]);
  }, [selectedRows, updateFiles]);

  const handleDeselectAllRows = useCallback(() => {
    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }
    setSelectedRows([]);
  }, []);

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      updateFiles((prevFiles) => {
        return prevFiles.map((item) => {
          if (item.id === data.id) {
            const updatedItem = { ...item };

            if (colDef.field === "file_display_name") {
              const originalExtension = item.name
                .split(".")
                .pop()
                .toLowerCase();
              const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
              updatedItem.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
            }

            if (colDef.field === "docGroup") {
              updatedItem.docGroup = newValue;
              if (updatedItem.docType) {
                updatedItem.docType = null;
              }
            }

            if (colDef.field === "docType") {
              updatedItem.docType = newValue;
            }

            if (colDef.field === "reportEffectiveDate") {
              if (newValue instanceof Date) {
                updatedItem.reportEffectiveDate = newValue.toISOString();
              } else if (typeof newValue === "string" && newValue) {
                if (!newValue.includes("T") || !newValue.endsWith("Z")) {
                  const dateObj = new Date(newValue);
                  if (!isNaN(dateObj.getTime())) {
                    updatedItem.reportEffectiveDate = dateObj.toISOString();
                  }
                }
              } else {
                updatedItem.reportEffectiveDate = newValue;
              }
            }

            if (colDef.field === "reportGuidelineExpirationDays") {
              updatedItem.reportGuidelineExpirationDays = newValue;
            }

            return updatedItem;
          }
          return item;
        });
      });

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [updateFiles],
  );

  const handleMetadataUpdate = useCallback(
    ({
      newDocGroup,
      newDocType,
      newEffectiveDate,
      newGuidelineExpirationDays,
    }) => {
      if (!selectedRows || selectedRows.length === 0) return;
      const selectedRowIds = selectedRows.map((row) => row.id);

      updateFiles((prevFiles) => {
        return prevFiles.map((row) => {
          if (!selectedRowIds.includes(row.id)) return row;
          const updatedRow = { ...row };

          if (newDocGroup !== undefined) {
            updatedRow.docGroup = newDocGroup;
            if (newDocType === undefined) {
              updatedRow.docType = null;
            }
          }

          if (newDocType !== undefined) {
            updatedRow.docType = newDocType;
          }

          if (newEffectiveDate !== undefined) {
            updatedRow.reportEffectiveDate =
              newEffectiveDate instanceof Date
                ? newEffectiveDate.toISOString()
                : newEffectiveDate;
          }

          if (newGuidelineExpirationDays !== undefined) {
            updatedRow.reportGuidelineExpirationDays =
              newGuidelineExpirationDays;
          }

          return updatedRow;
        });
      });

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [selectedRows, updateFiles],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
    };
  }, []);

  const toggleMinimize = useCallback((props) => {
    setMinimized((prev) => !prev);
    handleDeleteSelectedRows();
  }, []);

  if (isEmpty(files)) {
    return null;
  }
  return (
    <Flx
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 0,
        mt: 0,
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        zIndex: 111111,
        background: "#ffffff",
        boxShadow: "0 0 40px 0px #00000030",
      }}
    >
      <Flx
        fw
        jb
        ac
        sx={{
          background: "#ffffff",
          p: 1,
          pr: 2,
          borderBottom: `1px solid ${grey[200]}`,
          zIndex: 1111,
          borderTop: `1px solid ${grey[300]}`,
        }}
      >
        <IconButton onClick={toggleMinimize} size="small">
          {minimized ? (
            <KeyboardDoubleArrowDownOutlined />
          ) : (
            <KeyboardDoubleArrowUpOutlined />
          )}
        </IconButton>
        <Txt bold sx={{ p: 1 }}>
          Added Files ({size(files)})
        </Txt>

        <Flx end flexGrow={1} gap={1}>
          <Button onClick={onClearFilesClick} variant="outlined" size="large">
            Clear Files
          </Button>
          <Button size="large" onClick={handleUploadFiles} loading={loading}>
            Upload Files
          </Button>
        </Flx>
      </Flx>
      {isEmpty(selectedRows) || minimized ? null : (
        <BulkActionsToolbar
          files={files}
          selectedRows={selectedRows}
          onDeleteSelectedRows={handleDeleteSelectedRows}
          onMetadataUpdate={handleMetadataUpdate}
          onDeselectAllRows={handleDeselectAllRows}
        />
      )}
      {minimized ? null : (
        <Flx
          column
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            position: "relative",
            maxHeight: "60vh",
          }}
        >
          <AgGridTableWrapper height="100%" simpleTable size={4}>
            <AgGridReact
              ref={ref}
              getRowId={(params) => params.data.id}
              rowData={files}
              columnDefs={columnDefs}
              singleClickEdit
              columnTypes={columnTypes}
              onSelectionChanged={handleRowSelectionChange}
              domLayout={"autoHeight"}
              onCellValueChanged={handleCellValueChanged}
              stopEditingWhenCellsLoseFocus
              rowSelection={
                minimized
                  ? {}
                  : {
                      mode: "multiRow",
                    }
              }
            />
          </AgGridTableWrapper>
        </Flx>
      )}
    </Flx>
  );
};

const BulkActionsToolbar = ({
  files,
  selectedRows,
  onDeselectAllRows,
  onDeleteSelectedRows,
  onMetadataUpdate,
}) => {
  const [bulkDocGroup, setBulkDocGroup] = useState(null);
  const [bulkDocType, setBulkDocType] = useState(null);
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState("");
  const [bulkNumExpirationDays, setBulkNumExpirationDays] = useState(null);

  const onGroupChange = ({ docGroup, docType }) => {
    setBulkDocGroup(docGroup);
    setBulkDocType(docType);
    onMetadataUpdate({ newDocGroup: docGroup, newDocType: docType });
  };

  const onEffectiveDateChange = (date) => {
    setBulkNumExpirationDays(date);
    onMetadataUpdate({ newEffectiveDate: date });
  };

  const onNumExpirationDaysChange = (num) => {
    setBulkNumExpirationDays(num);
    onMetadataUpdate({ newGuidelineExpirationDays: num });
  };

  useEffect(() => {
    if (isEmpty(selectedRows)) {
      setBulkDocGroup(null);
      setBulkDocType(null);
      setBulkEffectiveDate(null);
    }
  }, [selectedRows]);

  if (isEmpty(selectedRows)) {
    return null;
  }
  return (
    <Flx
      fw
      column
      sx={{
        background: grey[100],
        p: 2,
        pt: 1,
        zIndex: "1111",
        ".MuiSelect-select, .MuiInputBase-root, .txt": {
          fontSize: "12px",
        },
      }}
    >
      <Flx fw jb ac gap={1} sx={{ mb: 0.5 }}>
        <Txt bold>{`Selected (${size(selectedRows)} of ${size(files)})`}</Txt>
        <Flx ac gap={1}>
          <Tooltip
            title={
              size(selectedRows) === 1
                ? "Delete File"
                : `Delete ${size(selectedRows)} Files`
            }
            disableInteractive
          >
            <Badge
              color="error"
              overlap="circular"
              badgeContent={size(selectedRows)}
              sx={{
                ".MuiBadge-badge": {
                  minWidth: "16px",
                  height: "16px",
                  fontSize: "10px",
                },
              }}
            >
              <IconButton
                onClick={onDeleteSelectedRows}
                color="error"
                variant="outlined"
                size="small"
              >
                <DeleteOutlineOutlined
                  sx={{ fontSize: "18px" }}
                  className="thin-8"
                />
              </IconButton>
            </Badge>
          </Tooltip>
          <Tooltip
            placement="top-start"
            title={`Deselect All`}
            disableInteractive
          >
            <IconButton
              onClick={onDeselectAllRows}
              variant="outlined"
              size="small"
            >
              <CloseOutlined sx={{ fontSize: "18px" }} className="thin-8" />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>
      <Flx fw gap={1.5}>
        <InputWrapper fullWidth label="Document Assignment">
          <SelectFileAssignmentInput
            placeholder="Document Group"
            docGroup={bulkDocGroup}
            docType={bulkDocType}
            onChange={onGroupChange}
            sx={{ width: "100%", minWidth: "120px" }}
          />
        </InputWrapper>
        <InputWrapper label="Report Effective Date">
          <DateInput
            fullWidth={false}
            value={bulkEffectiveDate}
            placeholder="Effective Date"
            onChange={onEffectiveDateChange}
            sx={{ width: "160px", minWidth: "140px" }}
          />
        </InputWrapper>

        <InputWrapper label="Report Expiration Days">
          <NumberInput
            value={bulkNumExpirationDays}
            placeholder="Num. of Days"
            onChange={onNumExpirationDaysChange}
            sx={{ width: "160px", minWidth: "140px" }}
          />
        </InputWrapper>
      </Flx>
    </Flx>
  );
};

const DocGroupVerticalNav = ({ docGroupFilter, setDocGroupFilter }) => {
  const [show, setShow] = useState(true);
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        background: "#ffffff",
        p: 1.2,
        borderLeft: `1px solid ${grey[300]}`,
      }}
    >
      <Flx
        column
        sx={{
          ".toggle-tab-switcher-root": {
            gap: 0,
          },
          ".Mui-selected": {
            backgroundColor: `${blue[50]} !important`,
          },
          ".MuiButtonBase-root": {
            fontSize: " 11px !important",
            // py: "3px !important",
            // px: "4px !important",
          },
        }}
      >
        <Flx fw jb ac g={0} sx={{ height: "40px" }}>
          {show && (
            <Txt
              bold
              fontSize={12}
              sx={{
                // p: "6px !important",
                visibility: show ? "visible" : "hidden",
              }}
            >
              Categories
            </Txt>
          )}
          <IconButton size="small" onClick={() => setShow((prev) => !prev)}>
            <EastOutlined
              sx={{
                fontSize: "14px",
                transition: "transform 0.3s",
                transform: `rotate(${show ? 0 : -180}deg)`,
              }}
            />
          </IconButton>
        </Flx>
        {show && (
          <ToggleTabSwitcher
            orientation="vertical"
            value={docGroupFilter}
            onChange={setDocGroupFilter}
            tabs={["All Categories", ...Object.keys(fileDocGroupAndTypes)]}
          />
        )}
      </Flx>
    </Box>
  );
};

const ModalRoot = ({ show, onClose, children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const styles = useMemo(() => {
    return {
      modalBody: {
        p: 0,
        pt: 0,
        borderTop: `1px solid ${grey[300]}`,
        ".MuiSelect-select": {
          fontSize: "12px",
        },
        ".ag-popup": {
          position: "absolute !important",
        },
        ".ag-rich-select": {
          position: "absolute !important",
        },
        ".ag-popup-child": {
          position: "absolute !important",
        },
      },
      body: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        overflow: "hidden",
        height: "100%",
      },
    };
  }, []);
  return (
    <BasicModal
      allowFullScreen
      disableAutoMobile={false}
      icon={<FileUploadOutlined />}
      title={"Upload Loan Documents"}
      titleEndContent={
        <Flx ac g={1} sx={{ ml: 2.4 }}>
          {/* <Txt secondary>{`#${loanDrilldown?.loanNumber}`}</Txt> */}

          <ChipItem
            sx={{ border: `1px solid ${grey[200]}` }}
            value={
              <>
                Loan # <b>{loanDrilldown?.loanNumber}</b>
              </>
            }
          />
          {loanDrilldown?.sync_loan_id && (
            <ChipItem
              sx={{ border: `1px solid ${grey[200]}` }}
              value={
                <>
                  Sync ID: <b>{loanDrilldown?.sync_loan_id}</b>
                </>
              }
            />
          )}
          <ChipItem
            sx={{ border: `1px solid ${grey[200]}` }}
            value={
              <>
                Name: <b>{loanDrilldown?.loanName}</b>
              </>
            }
          />
        </Flx>
      }
      //  titleBottomContent={
      //   <Flx ac g={1}>
      //     {/* <Txt secondary>{`#${loanDrilldown?.loanNumber}`}</Txt> */}
      //     <ChipItem
      //       sx={{ border: `1px solid ${grey[200]}` }}
      //       value={`#${loanDrilldown?.loanNumber}`}
      //     />

      //     <Txt secondary>{loanDrilldown?.loanName}</Txt>
      //   </Flx>
      // }
      show={show}
      defaultFullScreen
      onClose={onClose}
      maxWidth={"xl"}
      bodySx={styles.modalBody}
    >
      <Flx sx={styles.body}>{children}</Flx>
    </BasicModal>
  );
};
export default LoanBulkFileUploader;
